<template #default>
    <form
        v-if="status.detail && !loading"
        @submit.prevent="departmentUUID ? requestUpdate() : requestCreate()"
    >
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-sitemap mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.name')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.name"
                        :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
                        autocomplete="off"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.name")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.description')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.description"
                    ></b-form-textarea>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12 mb-2">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-factory mr-1"></i>
                    Company
                </h6>
            </div>
            <div class="col-md-12">
                <div class="table-responsive mb-3">
                    <table class="table table-sm border">
                        <thead class="thead-light">
                            <tr>
                                <th width="10%" class="text-center align-middle text-nowrap">#</th>
                                <th width="80%" class="align-middle">{{ $t('module.company') }}</th>
                                <th width="10%" class="text-center align-middle"><i class="mdi mdi-dots-horizontal"></i></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in createForm.companyUUIDs"
                                :key="index"
                                class="fade-in position-relative"
                            >
                                <td class="text-center align-middle text-nowrap">
                                    <span class="text-muted">{{ index + 1 }}</span>
                                </td>
                                <td class="text-nowrap">
                                    <multiselect
                                        v-model="item.companyUUID"
                                        track-by="value"
                                        label="text"
                                        select-label=""
                                        deselect-label=""
                                        :placeholder="$t('module.company')"
                                        :options="getSelectableCompanies(index)"
                                        :searchable="true"
                                        :allow-empty="false"
                                        class="cursor-pointer"
                                    />
                                </td>
                                <td class="text-center align-middle">
                                    <b-button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        @click="createForm.companyUUIDs.splice(index, 1)"
                                    >
                                        <i class="mdi mdi-close"></i>
                                    </b-button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td class="text-center align-middle">
                                    <b-button
                                        type="button"
                                        variant="info"
                                        size="sm"
                                        @click="createForm.companyUUIDs.push({companyUUID: null})"
                                    >
                                        <i class="mdi mdi-plus"></i>
                                    </b-button>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ departmentUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { departmentMethods, lookupMethods } from "@/state/api";
import { DEPARTMENT_MODULE } from "@/state/api/department.state";

export default {
    props: {
        departmentUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                name: { required },
            },
        };
    },
    components: { Multiselect },
    async created() {
        await this.lookupCompany({});
        await this.getDepartmentData();

        this.loading = false;
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            loading: !!this.departmentUUID,
        };
    },
    methods: {
        ...departmentMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: "",
                name: "",
                description: "",
                companyUUIDs: [],
            };
        },
        async getDepartmentData() {
            if (this.departmentUUID) {
                await this.getDepartment({ uuid: this.departmentUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                name: this.data.name,
                                description: this.data.description,
                                companyUUIDs: (this.data.companies ?? []).map(item => ({
                                    companyUUID: item ? {
                                        value: item.uuid,
                                        text: item.name,
                                    } : null,
                                })),
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    companyUUIDs: (this.createForm.companyUUIDs ?? []).map(item => item.companyUUID ? item.companyUUID.value : null),
                };
                this.createDepartment({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    companyUUIDs: (this.createForm.companyUUIDs ?? []).map(item => item.companyUUID ? item.companyUUID.value : null),
                };
                this.updateDepartment({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
        getSelectableCompanies(index) {
            const selected = this.createForm.companyUUIDs
                .filter((item, i) => i !== index && item.companyUUID)
                .map(item => item.companyUUID.value);
            return this.companyDdl.filter(item => !selected.includes(item.value));
        },
    },
    computed: {
        ...mapState(DEPARTMENT_MODULE, ['status']),
        companyDdl() {
            return this.$store.state.api.lookup.companyDdl || [];
        },
        data() {
            return this.$store.state.api.department.details;
        },
    },
}
</script>
